import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';

const Section = ({ title, children, description }) => (
  <div className="border border-gray-200 rounded-lg p-6">
    <h3 className="text-lg font-medium mb-1">{title}</h3>
    {description && <p className="text-xs text-gray-500 mb-4">{description}</p>}
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} className={`w-full border border-gray-300 rounded px-3 py-2 text-sm ${props.className || ''}`} />
);

const Btn = ({ children, ...props }) => (
  <button {...props} className={`px-4 py-2 bg-black text-white rounded text-sm hover:bg-gray-800 disabled:opacity-50 ${props.className || ''}`}>
    {children}
  </button>
);

const SecurityPanel = () => {
  // Change password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  // 2FA
  const [twofa, setTwofa] = useState({ enabled: false, enrolled_at: null });
  const [setupData, setSetupData] = useState(null); // { secret, provisioning_uri, qr_data_url }
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [twofaMsg, setTwofaMsg] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // Audit
  const [audit, setAudit] = useState([]);
  const [auditBusy, setAuditBusy] = useState(false);

  const loadTwofa = async () => {
    try { setTwofa(await api.request('/api/admin/2fa/status')); } catch (e) { /* ignore */ }
  };
  const loadAudit = async () => {
    setAuditBusy(true);
    try { setAudit(await api.request('/api/admin/audit?limit=100')); } catch (e) { /* ignore */ } finally { setAuditBusy(false); }
  };

  useEffect(() => { loadTwofa(); loadAudit(); }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg('');
    if (newPw.length < 12) { setPwMsg('New password must be at least 12 characters.'); return; }
    if (newPw !== confirmPw) { setPwMsg('Passwords do not match.'); return; }
    setPwBusy(true);
    try {
      await api.request('/api/admin/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      setPwMsg('Password updated. You\'ll need it on next login.');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      loadAudit();
    } catch (err) {
      setPwMsg(err.message || 'Failed to change password.');
    } finally {
      setPwBusy(false);
    }
  };

  const handle2faSetup = async () => {
    setTwofaMsg('');
    try {
      const data = await api.request('/api/admin/2fa/setup', { method: 'POST' });
      setSetupData(data);
    } catch (err) {
      setTwofaMsg(err.message || 'Setup failed.');
    }
  };

  const handle2faEnable = async (e) => {
    e.preventDefault();
    setTwofaMsg(''); setEnrollBusy(true);
    try {
      await api.request('/api/admin/2fa/enable', {
        method: 'POST',
        body: JSON.stringify({ code: enrollCode.trim() }),
      });
      setSetupData(null); setEnrollCode('');
      setTwofaMsg('2FA enabled. You\'ll need it on next login.');
      loadTwofa(); loadAudit();
    } catch (err) {
      setTwofaMsg(err.message || 'Enable failed.');
    } finally {
      setEnrollBusy(false);
    }
  };

  const handle2faDisable = async (e) => {
    e.preventDefault();
    setTwofaMsg('');
    try {
      await api.request('/api/admin/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ code: disableCode.trim() }),
      });
      setDisableCode('');
      setTwofaMsg('2FA disabled.');
      loadTwofa(); loadAudit();
    } catch (err) {
      setTwofaMsg(err.message || 'Disable failed.');
    }
  };

  return (
    <div className="space-y-6" data-testid="security-panel">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Security</h2>
        <p className="text-sm text-gray-600 max-w-3xl">
          Manage your admin password, two-factor authentication, and review the admin audit log.
        </p>
      </div>

      <Section title="Change password" description="Use at least 12 characters. After updating, you'll log in with the new password.">
        <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
          <Input type="password" placeholder="Current password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required data-testid="current-password-input" />
          <Input type="password" placeholder="New password (12+ chars)" value={newPw} onChange={(e) => setNewPw(e.target.value)} required data-testid="new-password-input" />
          <Input type="password" placeholder="Confirm new password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required data-testid="confirm-password-input" />
          {pwMsg && <div className="text-sm" data-testid="pw-msg">{pwMsg}</div>}
          <Btn type="submit" disabled={pwBusy} data-testid="change-password-btn">{pwBusy ? 'Saving…' : 'Change password'}</Btn>
        </form>
      </Section>

      <Section title={`Two-factor authentication (TOTP)`} description="Scan the QR with Google Authenticator, 1Password, Authy, or any TOTP app. You'll be asked for a 6-digit code after entering your password.">
        <div className="mb-3 text-sm">
          Status:{' '}
          <span className={`font-medium ${twofa.enabled ? 'text-green-700' : 'text-amber-700'}`} data-testid="twofa-status">
            {twofa.enabled ? 'Enabled' : 'Disabled'}
          </span>
          {twofa.enrolled_at && <span className="text-xs text-gray-500 ml-2">since {twofa.enrolled_at}</span>}
        </div>

        {!twofa.enabled && !setupData && (
          <Btn onClick={handle2faSetup} data-testid="twofa-setup-btn">Start 2FA setup</Btn>
        )}

        {!twofa.enabled && setupData && (
          <div className="mt-2 space-y-3 max-w-md">
            <div className="flex items-start gap-4">
              <img src={setupData.qr_data_url} alt="2FA QR" className="w-40 h-40 border rounded bg-white" data-testid="twofa-qr" />
              <div className="text-xs text-gray-600 flex-1">
                <div className="mb-2">Or enter the secret manually:</div>
                <div className="font-mono bg-gray-50 border rounded p-2 break-all" data-testid="twofa-secret">{setupData.secret}</div>
              </div>
            </div>
            <form onSubmit={handle2faEnable} className="flex gap-2">
              <Input type="text" maxLength={6} inputMode="numeric" pattern="[0-9]*"
                placeholder="Enter 6-digit code from app" value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))}
                className="font-mono tracking-widest text-center" data-testid="twofa-enroll-input" />
              <Btn type="submit" disabled={enrollBusy || enrollCode.length < 6} data-testid="twofa-enable-btn">
                {enrollBusy ? 'Enabling…' : 'Enable'}
              </Btn>
            </form>
          </div>
        )}

        {twofa.enabled && (
          <form onSubmit={handle2faDisable} className="flex gap-2 max-w-md mt-2">
            <Input type="text" maxLength={6} inputMode="numeric" pattern="[0-9]*"
              placeholder="Enter current 6-digit code to disable" value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
              className="font-mono tracking-widest text-center" data-testid="twofa-disable-input" />
            <button type="submit" disabled={disableCode.length < 6} className="px-4 py-2 border border-red-300 text-red-700 rounded text-sm hover:bg-red-50 disabled:opacity-50" data-testid="twofa-disable-btn">
              Disable
            </button>
          </form>
        )}

        {twofaMsg && <div className="mt-3 text-sm" data-testid="twofa-msg">{twofaMsg}</div>}
      </Section>

      <Section title="Audit log" description="Last 100 admin events: logins, lockouts, password changes, 2FA changes, API key activity.">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-gray-500">{audit.length} events</div>
          <button onClick={loadAudit} className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded" data-testid="refresh-audit-btn">
            {auditBusy ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        <div className="overflow-x-auto max-h-96 border border-gray-100 rounded">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">When (UTC)</th>
                <th className="text-left px-3 py-2">Event</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody data-testid="audit-rows">
              {audit.map((e, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-1.5 font-mono">{(e.at || '').replace('T', ' ').slice(0, 19)}</td>
                  <td className="px-3 py-1.5">{e.event}</td>
                  <td className="px-3 py-1.5">{e.email || ''}</td>
                  <td className="px-3 py-1.5 font-mono">{e.ip || ''}</td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">No events yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
};

export default SecurityPanel;
